import { useCallback, useMemo } from 'react';
import {
  Link as MuiLink, Grid, Typography, Stack, IconButton,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import LinkIcon from '@mui/icons-material/Link';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { faSquareFacebook, faTwitter } from '@fortawesome/free-brands-svg-icons';
import { Formik } from 'formik';
import { useImperativeClipboard } from '@client/hooks/useClipboardCopy';
import { buildURIFromParams, getShowLink } from '@sda/utils/url';
import { showSnackbar } from '@client/listeners/lib/snackbar';
import { Form, TextField } from '@client/components/forms';
import { Button } from '@client/components/common/Button';
import { useDispatch, useInstanceSelector, useListeners } from '@client/hooks/redux.hooks';
import { Modal } from '@client/components/globals/Modal';
import { startShareShow } from '@client/slices/entities/shows.reducer';
import { FontAwesomeIcon } from '@client/components/common/FontAwesomeIcon';
import { getShowById } from '@client/selectors/entities/shows';
import { EmailSchema } from './email.schema';
import { shareShowListeners } from './share-show.listeners';

const useStyles = makeStyles()((theme) => ({
  root: {
    width: '100%',
  },
  linkInput: {
    width: '100%',
  },
  linkIcon: {
    color: theme.palette.grey[500],
    fontSize: 24,
    marginRight: theme.spacing(1 / 2),
    transform: 'rotate(-45deg)',
  },
  shareToolbar: {
    '& > .MuiIconButton-root': {
      marginRight: theme.spacing(1),
    },
  },
}));

interface ShareModalProps {
  readonly showId: string;
}

export const ShareModal: React.FC<ShareModalProps> = ({ showId }) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();

  const show = useInstanceSelector(getShowById, showId);

  const copyUrl = getShowLink(show, 'copy');
  const handleCopy = useImperativeClipboard(copyUrl, () => showSnackbar('Link copied to clipboard.'));

  useListeners(shareShowListeners);

  const handleEmailSend = useCallback(({ email }: { email: string }) => {
    dispatch(startShareShow({ showId, email }));
  }, [dispatch, showId]);

  const facebookUrl = useMemo(() => {
    const baseUrl = 'https://www.facebook.com/sharer/sharer.php';

    return buildURIFromParams(baseUrl, {
      u: getShowLink(show, 'facebook'),
    });
  }, [show]);

  const twitterUrl = useMemo(() => {
    const baseUrl = 'https://twitter.com/intent/tweet';

    return buildURIFromParams(baseUrl, {
      text: '',
      url: getShowLink(show, 'twitter'),
      via: 'sidedooraccess',
    });
  }, [show]);

  const webShareData = useMemo(() => ({
    url: getShowLink(show, 'web'),
  }), [show]);

  const canShare = useMemo(() => {
    if (!navigator.share) {
      return false;
    }

    if (!navigator.canShare) {
      // Certain browsers support web share but not the canShare utility
      // For these browsers, we just have to try sharing anyway
      return true;
    }

    return navigator.canShare(webShareData);
  }, [webShareData]);

  const handleWebShareClick = useCallback(() => {
    if (canShare) {
      void navigator.share(webShareData);
    }
  }, [canShare, webShareData]);

  return (
    <Stack
      spacing={3}
      className={classes.root}
    >
      <div>
        <Typography
          variant="body2"
          color="textSecondary"
          gutterBottom
        >
          Social
        </Typography>

        <div className={classes.shareToolbar}>
          <IconButton
            target="_blank"
            href={facebookUrl}
            rel="noreferrer"
            color="inherit"
            size="medium"
            edge="start"
          >
            <FontAwesomeIcon icon={faSquareFacebook} />
          </IconButton>

          <IconButton
            target="_blank"
            href={twitterUrl}
            rel="noreferrer"
            color="inherit"
            size="medium"
          >
            <FontAwesomeIcon icon={faTwitter} />
          </IconButton>

          {Boolean(canShare) && (
            <IconButton
              onClick={handleWebShareClick}
              color="default"
              size="medium_inline"
              edge="end"
            >
              <MoreHorizIcon />
            </IconButton>
          )}
        </div>
      </div>

      <div>
        <Typography
          variant="body2"
          color="textSecondary"
          gutterBottom
        >
          Copy Link
        </Typography>

        <div
          className={classes.linkInput}
        >
          <Grid
            container
            spacing={0}
            alignItems="center"
            justifyContent="flex-start"
            wrap="nowrap"
          >
            <Grid item>
              <LinkIcon className={classes.linkIcon} />
            </Grid>

            <Grid
              item
              zeroMinWidth
            >
              <MuiLink
                onClick={handleCopy}
                aria-label="Copy Link"
                variant="subtitle1"
                display="block"
                noWrap
              >
                {copyUrl}
              </MuiLink>
            </Grid>
          </Grid>
        </div>
      </div>

      <Formik
        onSubmit={handleEmailSend}
        initialValues={{ email: '' }}
        validationSchema={EmailSchema}
      >
        {({ values }) => (
          <Form>
            <Stack spacing={2}>
              <TextField
                label="Email"
                name="email"
                type="email"
                fullWidth
              />

              <div>
                <Button
                  scope="send-connect-share-email"
                  onSuccess={() => {
                    Modal.hide();
                    showSnackbar(`Show shared with ${values.email}.`);
                  }}
                  ButtonProps={{
                    disabled: !values.email,
                  }}
                >
                  Share via Email
                </Button>
              </div>
            </Stack>
          </Form>
        )}
      </Formik>
    </Stack>
  );
};

export default ShareModal;
